import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContractDocument } from "@/components/admin/ContractDocument";
import { getContract, resendContractLink, cancelContract } from "@/lib/contracts.functions";
import type { EmploymentContract } from "@/lib/contracts";

export const Route = createFileRoute("/_authenticated/admin/contracts/$id")({
  head: () => ({ meta: [{ title: "Employment contract | Admin" }] }),
  component: ContractPage,
});

const LABEL: Record<string, string> = { awaiting_employee: "Waiting for employee's signature", signed: "Signed by both", cancelled: "Cancelled" };

function ContractPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getContract);
  const resendFn = useServerFn(resendContractLink);
  const cancelFn = useServerFn(cancelContract);
  const { data: c, isLoading, error } = useQuery({ queryKey: ["contract", id], queryFn: () => getFn({ data: { id } }) as Promise<EmploymentContract> });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error || !c) return <p className="text-sm text-destructive">Contract not found.</p>;
  const run = (fn: () => Promise<unknown>) => fn().catch((e) => toast.error((e as Error).message));
  return (
    <div className="space-y-4">
      <Link to="/admin/contracts" className="text-sm text-muted-foreground hover:underline">← All contracts</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">{c.employee_name}</h1>
        <Badge variant={c.status === "signed" ? "default" : "secondary"}>{LABEL[c.status]}</Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => run(async () => (await import("@/lib/contract-pdf")).downloadContractPdf(c))}>Download PDF</Button>
        <Button variant="outline" onClick={() => run(async () => (await import("@/lib/contract-pdf")).printContractPdf(c))}>Print</Button>
        {c.status === "awaiting_employee" && <>
          <Button variant="outline" onClick={() => run(async () => { const r = await resendFn({ data: { id } }); toast.success(`Signing link sent again to ${r.to}`); })}>Resend link to employee</Button>
          <Button variant="outline" onClick={() => { if (confirm("Cancel this contract? The employee's link will stop working.")) run(async () => { await cancelFn({ data: { id } }); toast.success("Contract cancelled"); qc.invalidateQueries({ queryKey: ["contract", id] }); }); }}>Cancel contract</Button>
        </>}
      </div>
      {c.status === "signed" && <p className="text-sm text-muted-foreground">The signed copy was {c.signed_emailed_at ? "emailed to you and the employee" : "saved — the email copy could not be sent, so download it here"}.</p>}
      <ContractDocument {...c} />
    </div>
  );
}
